import { useState } from 'react'
import { useParams } from 'react-router-dom'
import TestPlanGrid from '../../components/testplan/TestPlanGrid'
import { useExecution } from '../../hooks/useExecution'
import api from '../../lib/axios'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const STATUS_COLORS = {
  draft:       'text-white/40 bg-white/6 border-white/10',
  in_progress: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  review:      'text-amber-400 bg-amber-500/10 border-amber-500/20',
  complete:    'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  archived:    'text-gray-400 bg-gray-500/10 border-gray-500/20',
}

export default function TestPlanExecutionPage() {
  const { planId, executionId } = useParams()
  const { execution, loading, updateStatus } = useExecution(executionId)

  const [generatingPdf, setGeneratingPdf] = useState(null)

  const generatePdf = async (type) => {
    setGeneratingPdf(type)
    const toastId = toast.loading('Generating PDF…')
    try {
      if (type === 'non-technical') {
        // Trigger non-technical doc generation via API
        const { data } = await api.post(`/repositories/${execution?.testPlan?.repository_id}/generate-pdf/non-technical`)
        toast.success('Non-technical PDF ready!', { id: toastId })
        window.open(data.download_url, '_blank')
      } else if (type === 'role-test-plan') {
        const { data } = await api.post(`/test-plans/${execution?.test_plan_id}/generate-pdf/role-test-plan`)
        toast.success('Role test plan PDF ready!', { id: toastId })
        window.open(data.download_url, '_blank')
      }
    } catch {
      toast.error('PDF generation failed.', { id: toastId })
    } finally {
      setGeneratingPdf(null)
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
    </div>
  )

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'linear-gradient(180deg, #0d0521 0%, #07020f 100%)', minHeight: '100vh' }}>

      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="px-6 pt-6 pb-4 border-b border-white/8 flex-shrink-0">
        <div className="flex items-start justify-between gap-4">

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <span className={clsx(
                'text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border',
                STATUS_COLORS[execution?.status] ?? STATUS_COLORS.draft
              )}>
                {execution?.status ?? 'draft'}
              </span>
              {execution?.version && (
                <span className="text-xs text-white/30 font-mono">{execution.version}</span>
              )}
              {execution?.environment && (
                <span className="text-xs text-white/30">{execution.environment}</span>
              )}
            </div>
            <h1 className="font-display font-bold text-white text-xl truncate">
              {execution?.title ?? 'Test Plan Execution'}
            </h1>
            {execution?.description && (
              <p className="text-sm text-white/40 mt-1">{execution.description}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">

            {/* PDF Generation buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => generatePdf('non-technical')}
                disabled={!!generatingPdf}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs font-semibold hover:bg-blue-500/20 transition-all disabled:opacity-50"
              >
                {generatingPdf === 'non-technical'
                  ? <><span className="w-3 h-3 border border-blue-300/30 border-t-blue-300 rounded-full animate-spin" />Generating…</>
                  : <><span>📄</span> Non-Technical Doc</>
                }
              </button>

              <button
                onClick={() => generatePdf('role-test-plan')}
                disabled={!!generatingPdf}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-semibold hover:bg-purple-500/20 transition-all disabled:opacity-50"
              >
                {generatingPdf === 'role-test-plan'
                  ? <><span className="w-3 h-3 border border-purple-300/30 border-t-purple-300 rounded-full animate-spin" />Generating…</>
                  : <><span>📋</span> Role Test Plan</>
                }
              </button>
            </div>

            {/* Status changer */}
            <StatusDropdown status={execution?.status} onUpdate={updateStatus} />
          </div>
        </div>
      </div>

      {/* ── Grid ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden">
        <TestPlanGrid executionId={parseInt(executionId)} />
      </div>
    </div>
  )
}

// ── Status changer dropdown ───────────────────────────────────────────────────
function StatusDropdown({ status, onUpdate }) {
  const [open, setOpen] = useState(false)
  const options = ['draft', 'in_progress', 'review', 'complete', 'archived']

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/6 border border-white/12 hover:bg-white/10 text-white/60 hover:text-white text-xs font-semibold transition-all"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
        </svg>
        Update Status
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-40 rounded-xl border border-white/12 bg-cosmic-900/98 backdrop-blur-xl shadow-2xl py-1 z-30"
          onMouseLeave={() => setOpen(false)}
        >
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => { onUpdate(opt); setOpen(false) }}
              className={clsx(
                'w-full text-left px-3 py-2 text-xs font-semibold capitalize transition-colors',
                status === opt ? 'text-purple-300 bg-purple-500/15' : 'text-white/50 hover:bg-white/5 hover:text-white'
              )}
            >
              {opt.replace('_', ' ')}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
